import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

/**
 * Custom hook for WebSocket connection
 * @param {string} companyId - Company ID to join company room
 * @param {Array} documentIds - Array of document IDs to listen for updates
 * @param {string} contractId - Contract ID (optional)
 * @param {string} quoteId - Quote ID (optional)
 * @param {string} invoiceId - Invoice ID (optional)
 * @returns {Object} - { socket, isConnected, error }
 */
export const useWebSocket = ({
  companyId,
  documentIds = [],
  contractId = null,
  quoteId = null,
  invoiceId = null,
} = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    // Get WebSocket URL from environment or construct from API base URL
    const apiUrl = import.meta.env.VITE_BASE_API || "";
    let wsUrl = apiUrl;

    // Remove trailing slash if present
    if (wsUrl.endsWith("/")) {
      wsUrl = wsUrl.slice(0, -1);
    }

    // Detect protocol from current page (for live site) or use API URL protocol
    const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
    const defaultProtocol = isSecure ? "https://" : "http://";

    // If API URL doesn't include protocol, add it based on current page protocol
    if (wsUrl && !wsUrl.startsWith("http://") && !wsUrl.startsWith("https://")) {
      wsUrl = `${defaultProtocol}${wsUrl}`;
    } else if (wsUrl && typeof window !== "undefined") {
      // If API URL has protocol but doesn't match current page, update it
      const currentProtocol = window.location.protocol === "https:" ? "https://" : "http://";
      if (wsUrl.startsWith("http://") && currentProtocol === "https://") {
        wsUrl = wsUrl.replace("http://", "https://");
      } else if (wsUrl.startsWith("https://") && currentProtocol === "http://") {
        wsUrl = wsUrl.replace("https://", "http://");
      }
    }

    // Extract host from URL
    try {
      const url = new URL(wsUrl);
      wsUrl = `${url.protocol}//${url.host}`;
    } catch (e) {
      console.error("Invalid API URL for WebSocket:", wsUrl);
      setError("Invalid WebSocket URL");
      return;
    }

    // Initialize socket connection
    // Socket.IO automatically uses wss:// when connecting to https:// URLs
    socketRef.current = io(wsUrl, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"], // Try websocket first, fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect
      timeout: 20000,
      forceNew: false,
      upgrade: true, // Allow upgrade from polling to websocket
    });

    const socket = socketRef.current;

    // Debug logging (remove in production if needed)
    if (process.env.NODE_ENV === "development") {
      console.log("WebSocket connecting to:", wsUrl);
      console.log("Socket.IO path:", "/api/socket.io");
      console.log("Current page protocol:", typeof window !== "undefined" ? window.location.protocol : "N/A");
    }
    
    // Connection event handlers
    socket.on("connect", () => {
      setIsConnected(true);
      setError(null);
      if (process.env.NODE_ENV === "development") {
        console.log("WebSocket connected successfully");
      }

      // Join company room
      if (companyId) {
        socket.emit("join-company", companyId);
      }

      // Join document rooms
      if (Array.isArray(documentIds) && documentIds.length > 0) {
        documentIds.forEach((docId) => {
          if (docId) {
            socket.emit("join-document", docId);
          }
        });
      }

      // Join contract/quote/invoice rooms
      if (contractId) {
        socket.emit("join-contract", contractId);
      }
      if (quoteId) {
        socket.emit("join-quote", quoteId);
      }
      if (invoiceId) {
        socket.emit("join-invoice", invoiceId);
      }
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    socket.on("connect_error", (err) => {
      console.error("WebSocket connection error:", err);
      console.error("Error details:", {
        message: err.message,
        type: err.type,
        description: err.description,
        context: err.context,
        wsUrl: wsUrl,
        path: "/api/socket.io",
      });
      setError(err.message || "Connection failed");
      setIsConnected(false);
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
      if (process.env.NODE_ENV === "development") {
        console.log(`WebSocket reconnection attempt #${attemptNumber}`);
      }
    });

    socket.on("reconnect_failed", () => {
      console.error("WebSocket reconnection failed after all attempts");
      setError("Failed to reconnect. Please refresh the page.");
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [companyId, JSON.stringify(documentIds), contractId, quoteId, invoiceId]);

  return {
    socket: socketRef.current,
    isConnected,
    error,
  };
};

/**
 * Hook to listen for document status updates
 * @param {Object} socket - Socket instance from useWebSocket
 * @param {Function} onStatusUpdate - Callback when document status updates
 * @param {Function} fetchData - Function to refetch data after update
 */
export const useDocumentStatusListener = (socket, onStatusUpdate, fetchData) => {
  useEffect(() => {
    if (!socket || !socket.connected) {
      return;
    }

    const handleStatusUpdate = (data) => {
      // Call the callback if provided
      if (onStatusUpdate && typeof onStatusUpdate === "function") {
        try {
          onStatusUpdate(data);
        } catch (error) {
          console.error("Error in onStatusUpdate callback:", error);
        }
      }

      // Refetch data to get latest state (always do this to ensure UI is updated)
      if (fetchData && typeof fetchData === "function") {
        setTimeout(() => {
          fetchData();
        }, 500); // Small delay to ensure backend has saved
      }
    };

    socket.on("document_status_updated", handleStatusUpdate);

    return () => {
      if (socket) {
        socket.off("document_status_updated", handleStatusUpdate);
      }
    };
  }, [socket, socket?.connected, onStatusUpdate, fetchData]);
};

